<template>
  <div class="reset-password-page">
    <div class="reset-password-container">
      <h2>重置密码</h2>
      <div class="reset-password-form">
        <div class="form-group">
          <label>邮箱</label>
          <div class="email-input-wrapper">
            <div class="email-input" :class="{ 'input-error': errors.email }">
              <input
                type="email"
                v-model="email"
                placeholder="请输入注册邮箱"
                :class="{ 'input-error': errors.email }"
              >
            </div>
            <div class="email-hint">
              <i class="fas fa-info-circle"></i>
              <span>如果用户注册时未提供邮箱，请联系客服在后台配置</span>
            </div>
          </div>
          <span class="error-message" v-if="errors.email">{{ errors.email }}</span>
        </div>

        <div class="form-group">
          <label>验证码</label>
          <div class="code-input-group" :class="{ 'input-error': errors.code }">
            <input
              type="text"
              v-model="code"
              placeholder="请输入验证码"
              maxlength="6"
              :class="{ 'input-error': errors.code }"
            >
            <button
              class="send-code-btn"
              :disabled="isSending || !email"
              @click="handleSendCode"
            >
              {{ isSending ? `${countdown}s 后重发` : '发送验证码' }}
            </button>
          </div>
          <span class="error-message" v-if="errors.code">{{ errors.code }}</span>
        </div>

        <div class="form-group">
          <label>新密码</label>
          <div class="password-input" :class="{ 'input-error': errors.password }">
            <input
              :type="showPassword ? 'text' : 'password'"
              v-model="password"
              placeholder="请设置 6-20 位密码"
              maxlength="20"
              :class="{ 'input-error': errors.password }"
            >
            <button class="toggle-password" @click="showPassword = !showPassword">
              <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
          <span class="error-message" v-if="errors.password">{{ errors.password }}</span>
        </div>

        <div class="form-group">
          <label>确认新密码</label>
          <div class="password-input" :class="{ 'input-error': errors.confirmPassword }">
            <input
              :type="showConfirmPassword ? 'text' : 'password'"
              v-model="confirmPassword"
              placeholder="请再次输入密码"
              maxlength="20"
              :class="{ 'input-error': errors.confirmPassword }"
            >
            <button class="toggle-password" @click="showConfirmPassword = !showConfirmPassword">
              <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
          <span class="error-message" v-if="errors.confirmPassword">{{ errors.confirmPassword }}</span>
        </div>

        <button class="reset-btn" @click="handleResetPassword">重置密码</button>

        <div class="back-to-login">
          <router-link to="/login">返回登录</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { request } from '@/utils/request'

export default {
  name: 'ResetPasswordView',
  data() {
    return {
      email: '',
      code: '',
      password: '',
      confirmPassword: '',
      showPassword: false,
      showConfirmPassword: false,
      isSending: false,
      countdown: 60,
      timer: null,
      errors: {
        email: '',
        code: '',
        password: '',
        confirmPassword: ''
      }
    }
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer)
    }
  },
  methods: {
    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return re.test(email)
    },
    async handleSendCode() {
      if (!this.email) {
        this.errors.email = '请输入邮箱'
        return
      }
      if (!this.validateEmail(this.email)) {
        this.errors.email = '请输入正确的邮箱格式'
        return
      }
      this.errors.email = ''

      try {
        const data = await request('/api/auth/email/sendCode', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email: this.email, type: 2 })
        })

        if (data.ok) {
          this.$message.success('验证码已发送，请查收邮箱')
          this.startCountdown()
        } else {
          this.$message.error(data.msg || '发送验证码失败')
        }
      } catch (error) {
        console.error('发送验证码失败:', error)
        this.$message.error('发送验证码失败，请稍后重试')
      }
    },
    startCountdown() {
      this.isSending = true
      this.countdown = 60
      this.timer = setInterval(() => {
        if (this.countdown > 1) {
          this.countdown--
        } else {
          this.stopCountdown()
        }
      }, 1000)
    },

    stopCountdown() {
      this.isSending = false
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    validateForm() {
      let isValid = true
      this.errors = {
        email: '',
        code: '',
        password: '',
        confirmPassword: ''
      }

      if (!this.email) {
        this.errors.email = '请输入邮箱'
        isValid = false
      } else if (!this.validateEmail(this.email)) {
        this.errors.email = '请输入正确的邮箱格式'
        isValid = false
      }

      if (!this.code) {
        this.errors.code = '请输入验证码'
        isValid = false
      } else if (this.code.length !== 6) {
        this.errors.code = '验证码格式不正确'
        isValid = false
      }

      if (this.password.length < 6 || this.password.length > 20) {
        this.errors.password = '密码长度需在6-20位之间'
        isValid = false
      }

      if (this.password !== this.confirmPassword) {
        this.errors.confirmPassword = '两次输入的密码不一致'
        isValid = false
      }

      return isValid
    },
    async handleResetPassword() {
      if (!this.validateForm()) {
        this.$message.warning('请检查输入信息是否正确')
        return
      }

      try {
        const data = await request('/api/auth/email/reset/password', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: this.email,
            code: this.code,
            password: this.password
          })
        })
        if (data.ok) {
          this.$message.success('密码重置成功')
          this.$router.push('/login')
        } else {
          this.$message.error(data.msg || '密码重置失败')
        }
      } catch (error) {
        console.error('密码重置失败:', error)
        this.$message.error('密码重置失败，请稍后重试')
      }
    }
  }
}
</script>

<style scoped>
.reset-password-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  padding: 20px;
}

.reset-password-container {
  background: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  width: 100%;
  max-width: 400px;
}

.reset-password-container h2 {
  text-align: center;
  margin-bottom: 30px;
  color: #333;
  font-weight: 600;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
}

.email-input,
.password-input,
.code-input-group {
  display: flex;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
  transition: border-color 0.3s ease;
}

.email-input-wrapper {
  position: relative;
}

.email-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #f8f9fa;
  border-radius: 4px;
  font-size: 12px;
  color: #666;
  border-left: 3px solid #2196F3;
}

.email-hint i {
  color: #2196F3;
  font-size: 14px;
}

.email-input.input-error,
.password-input.input-error,
.code-input-group.input-error {
  border-color: #f56c6c;
}

.email-input.input-error input,
.password-input.input-error input,
.code-input-group.input-error input {
  background-color: rgba(245, 108, 108, 0.05);
}

.email-input input,
.password-input input,
.code-input-group input {
  flex: 1;
  padding: 12px;
  border: none;
  outline: none;
  font-size: 14px;
  transition: all 0.3s ease;
}

.email-input input.input-error,
.password-input input.input-error,
.code-input-group input.input-error {
  color: #f56c6c;
}

.send-code-btn {
  padding: 0 15px;
  border: none;
  background: #f0f0f0;
  color: #666;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.3s;
  white-space: nowrap;
  border-left: 1px solid #ddd;
}

.send-code-btn:hover:not(:disabled) {
  background: #e0e0e0;
  color: #333;
}

.send-code-btn:disabled {
  cursor: not-allowed;
  color: #999;
}

.toggle-password {
  padding: 0 15px;
  border: none;
  background: none;
  cursor: pointer;
  color: #666;
}

.error-message {
  color: #f56c6c;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

.reset-btn {
  width: 100%;
  padding: 12px;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.reset-btn:hover {
  background: #43A047;
}

.back-to-login {
  text-align: center;
  margin-top: 20px;
}

.back-to-login a {
  color: #4CAF50;
  text-decoration: none;
  font-size: 14px;
}

.back-to-login a:hover {
  text-decoration: underline;
}
</style>
